import * as THREE from "three";
import { getGraspState } from "../../stores/graspStore";
import type { BodyUUID } from "../../types/scene";
import type { IkChain, IkKinematicsState } from "../types";
import { quatToMj } from "../orientationTargets";
import { solvePositionIk } from "../solver";
import { rankGraspsWorldZUp } from "./graspRanking";
import type { RankedGraspWorldZ } from "./graspRanking";
import type { PickPlaceStage } from "./pickPlaceServoConfig";

export function resolveGraspCandidatesWorldZ(args: {
  isPickStage: boolean;
  targetBodyUuid: BodyUUID;
  resolveRuntimeObject: (bodyUuid: BodyUUID) => THREE.Object3D | null;
  fkSeed: IkKinematicsState;
  yToZ: THREE.Quaternion;
  zToY: THREE.Quaternion;
}): RankedGraspWorldZ[] | null {
  const { isPickStage, targetBodyUuid, resolveRuntimeObject, fkSeed, yToZ, zToY } = args;
  if (!isPickStage) return null;
  const grasps = getGraspState().graspsByBodyUuid[targetBodyUuid];
  if (!grasps || grasps.length === 0) return null;
  const bodyObj = resolveRuntimeObject(targetBodyUuid);
  if (!bodyObj) return null;
  const bodyWorldPosScene = new THREE.Vector3();
  const bodyWorldQuatScene = new THREE.Quaternion();
  bodyObj.getWorldPosition(bodyWorldPosScene);
  bodyObj.getWorldQuaternion(bodyWorldQuatScene);
  const eeWorldPosScene = fkSeed.endEffector.position.clone().applyQuaternion(zToY);
  return rankGraspsWorldZUp({
    grasps,
    bodyWorldPosScene,
    bodyWorldQuatScene,
    yToZ,
    eeWorldPosScene,
  });
}

export function selectReachableGraspWorldZ(args: {
  isPickStage: boolean;
  graspCandidatesWorldZ: RankedGraspWorldZ[] | null;
  lockedPickGrasp: RankedGraspWorldZ | null;
  stage: PickPlaceStage;
  graspCandidateOffset: number;
  approachHeight: number;
  chain: IkChain;
  seed: number[];
}): RankedGraspWorldZ | null {
  const {
    isPickStage,
    graspCandidatesWorldZ,
    lockedPickGrasp,
    stage,
    graspCandidateOffset,
    approachHeight,
    chain,
    seed,
  } = args;
  if (!isPickStage || !Array.isArray(graspCandidatesWorldZ) || graspCandidatesWorldZ.length === 0) {
    return null;
  }
  if (
    lockedPickGrasp &&
    (stage === "approach" || stage === "final" || stage === "close" || stage === "lift")
  ) {
    return lockedPickGrasp;
  }
  if (stage !== "approach" && stage !== "final" && stage !== "close") {
    const idx =
      ((graspCandidateOffset % graspCandidatesWorldZ.length) + graspCandidatesWorldZ.length) %
      graspCandidatesWorldZ.length;
    return graspCandidatesWorldZ[idx];
  }
  const feasibilityMax = Math.min(5, graspCandidatesWorldZ.length);
  for (let i = 0; i < feasibilityMax; i += 1) {
    const idx = (graspCandidateOffset + i) % graspCandidatesWorldZ.length;
    const cand = graspCandidatesWorldZ[idx];
    const probePos =
      stage === "approach"
        ? cand.positionZ.clone().add(new THREE.Vector3(0, 0, approachHeight))
        : cand.positionZ.clone();
    const probeAttempt = solvePositionIk(
      chain,
      {
        position: [probePos.x, probePos.y, probePos.z],
        orientation: quatToMj(cand.orientationZ),
      },
      seed,
      {
        maxIterations: 96,
        tolerance: 2e-3,
        rotationTolerance: 0.2,
        damping: 0.03,
        stepSize: 0.65,
        orientationWeight: 0.65,
      }
    );
    if (probeAttempt && Number.isFinite(probeAttempt.error) && probeAttempt.error <= 0.08) {
      return cand;
    }
  }
  const fallbackIdx =
    ((graspCandidateOffset % graspCandidatesWorldZ.length) + graspCandidatesWorldZ.length) %
    graspCandidatesWorldZ.length;
  return graspCandidatesWorldZ[fallbackIdx];
}
